import "./styles.scss";
import { pieLoader, wait, queryToObject, updateQuery, updateHistorySearchState, analytics } from "utils";

const embedsCache = new Map();

const fadeOutAnimation = [
  [
    { opacity: 1, scale: 1, translate: "0 0" },
    { opacity: 0, scale: 0.3, translate: "0 -1rem" },
  ], {
    duration: 300,
    easing: "ease",
  },
];

const fadeInAnimation = [
  [
    { opacity: 0, scale: 0.3, translate: "0 -1rem" },
    { opacity: 1, scale: 1, translate: "0 0" },
  ], {
    duration: 300,
    easing: "ease",
  },
];

export default function Embeds(containerId, rootSelectror = "body") {
  return new Promise(resolve => {

    const rootElement = document.querySelector(rootSelectror);
    const container = document.querySelector(containerId);
    const wrapper = document.createElement("div");

    const { open } = queryToObject(window.location.search);

    wrapper.className = "pie-player-embeds-wrapper";
    wrapper.append(pieLoader("center-absolute"));
    wrapper.append(container.firstElementChild);
    container.append(wrapper);

    let nextActiveEmbed;
    let currentActiveButton;
    let currentEmbedName = "initial";
    let activeEmbed = wrapper.lastElementChild;

    // Set initial (default) embed.
    embedsCache.set(currentEmbedName, activeEmbed);


    // Automatic embed change besed on URL.
    if (Boolean(open)) {
      const elementTarget = document.querySelector(`[data-uid="${open}"]`);
      const openTarget = elementTarget.dataset.embedTarget;

      // Open proper tab.
      const card = elementTarget.closest(".pie-player-list-infocard");
      card.children[0].click();

      updateEmbed(openTarget, elementTarget);
    }


    // Manual embed change.
    rootElement.addEventListener("click", async event => {
      const { embedTarget, embedType, uid } = event.target.dataset;
      const IS_NEW_TARGET = embedTarget && currentEmbedName !== embedTarget;
      const OUTBOUND_TRIGGER = currentEmbedName !== embedTarget && event.target.matches("a, button");

      if (IS_NEW_TARGET) {
        event.preventDefault();
        updateEmbed(embedTarget, event.target);

        // Update Query params to reflect current embed URL.
        updateHistorySearchState(updateQuery(window.location.search, "open", uid));

        // Send analytics event.
        analytics("embed_open", {
          embed_slug: uid,
          embed_type: embedType,
          embed_title: event.target.title,
        });
      }

      // User clicked on something outside Embeds controlls.
      else if (OUTBOUND_TRIGGER) {
        currentActiveButton?.classList.remove("active");
      }

    });


    async function updateEmbed(embedTarget, elementTarget) {

      currentEmbedName = embedTarget;

      // Handle tab buttons.
      currentActiveButton?.classList.remove("active");
      currentActiveButton = elementTarget;
      currentActiveButton.classList.add("active");

      window.scrollTo(0, 0);
      await wait(300); // Smooth out scroll transition.

      currentActiveButton.scrollIntoViewIfNeeded?.();
      container.classList.add("loading");

      await activeEmbed.animate(...fadeOutAnimation).finished;

      pausePlayingVideo(activeEmbed);

      activeEmbed.classList.add("hidden");
      nextActiveEmbed = await getIframe(embedTarget, wrapper);

      // Stop if user clicked on another embed button.
      if (currentEmbedName !== embedTarget) return;

      container.classList.remove("loading");
      nextActiveEmbed.classList.remove("hidden");

      await nextActiveEmbed.animate(...fadeInAnimation).finished;


      activeEmbed = nextActiveEmbed;
    }


    resolve(true);
  });
}

// ---- Helpers ----------------


function getIframe(src, root) {
  return new Promise(async (resolve, reject) => {

    let iframe = embedsCache.get(src);

    // Read from a cache.
    if (iframe instanceof HTMLElement) {
      // Trigger H5P iframe resize script.
      resizeH5P(iframe);
      // Done.
      resolve(iframe);
    }

    // Create new iframe.
    else if (iframe !== null) {

      iframe = document.createElement("iframe");
      iframe.onerror = error => reject(error);
      iframe.onload = async () => {
        // Update cache with loaded iframe.
        embedsCache.set(src, iframe);
        // Trigger H5P iframe resize script.
        resizeH5P(iframe);
        // Done.
        resolve(iframe);
      };

      iframe.src = src;
      iframe.frameborder = 0;
      iframe.className = "hidden";
      iframe.allowfullscreen = true;

      // Set loading state.
      embedsCache.set(src, null);
      root.append(iframe);

    }
  });
}


// When embed is type of H5P, we need to send ready message to trigger "resize" event.
function resizeH5P(iframe) {
  if (iframe.src.includes("h5p")) {
    iframe.contentWindow.postMessage({ context: "h5p", action: "ready" }, "*");
    iframe.classList.add("h5p");
  }
}

// Pause video when switching embeds.
function pausePlayingVideo(embed) {

  // Youtube embed.
  if (embed.src.includes("https://www.youtube.com")) {
    embed.contentWindow.postMessage(JSON.stringify({
      func: "pauseVideo",
      event: "command",
    }), "https://www.youtube.com");
  }

  // Pistacja embed (local files).
  else if (embed.src.includes("/images/videos")) {
    embed.contentWindow.document.body.getElementsByTagName("video")?.[0]?.pause();
  }
}
